import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { genererCanauxRuban, MATCAP_GAIN, CHROME_BOX } from './lib/matcap.js';
import { vdjColor, vdjBeatChroma, vdjCameraKick, VdjPost, VdjComfort, VDJ_DROP_FLASH } from './lib/vdjLook.js';

// Contrat du rig partagé : interdit. Le néon est sa propre lumière.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'mobius',
  family: 'geometric',
  danceability: 0.82,
  requiresTier: 0,
  energyLevel: 0.76,
  bpmMin: 110,
  bpmMax: 150,
  movement: 0.75,
  tonality: 0.55,
  enabled: true
};

// MOBIUS : un ruban de Möbius géant en néon qui tourne lentement au centre de
// l'écran ; un pavage de tuiles lumineuses, et des coureurs de lumière qui le
// parcourent sans fin, du « dessus » au « dessous » sans jamais franchir de
// bord. Techno, trance, progressive, techno mélodique. LOOK VDJ party.
//
// LE RUBAN : P(u, v) = c(u) + w v e(u), c le cercle de rayon R, e la
// direction de la section, tournée de θ(u) = k u / 2 autour de la tangente
// (k demi-torsions). Normales ANALYTIQUES (dérivées partielles exactes,
// produit vectoriel). Pour k impair, P(u + 2π, v) = P(u, -v) : écrite sur
// U dans [0, 4π) la même formule parcourt le ruban DEUX fois, la seconde sur
// l'autre « face » - c'est ainsi que sont dessinés le bord (une seule
// boucle néon, v = 1 sur 4π) et les coureurs (v fixe, U sur 4π : ils
// reviennent à leur départ après deux tours, critère 2).
//
// LA MÉTAMORPHOSE 1 -> 3 demi-torsions (phrase) et le déroulé du drop
// (k -> 0, l'anneau plat face caméra) : pour k non entier la section
// n'est pas refermée en u = 0 (décalage de 2πe modulo π). Le ruban est
// pincé à la couture en proportion du décalage (|sin(2π e)|^0.35) : la
// déchirure reste un point pendant les 2 temps de la métamorphose.
//
// LES COUREURS (R16) : le corps est intégré EN ABSCISSE CURVILIGNE depuis
// la tête : chaque échantillon recule de ds / |P_U| (vitesse locale exacte
// au paramètre), jamais de du constant : pas d'étirement dans la torsion
// (critère 3). Un ruban d'écran à profil capsule (cœur blanc, gaine).
//
// LES TUILES (fragment) : 24 x 4 sur la paramétrisation ; contour net de
// 1,5 px par fwidth ; fond noir laqué en matcap (R5) ; allumées par les
// coureurs (la tuile s'allume quand un coureur y entre, s'éteint en
// 2 temps - les deux passages d'un coureur, v et -v, sont testés), par la
// vague du temps et par le flash de rangée de la mesure.
//
// CADRAGE (R6, R19, critère 5) : le ruban tient dans la boule de rayon
// R + w autour du centre de composition ; vue de D, sa silhouette ne
// dépasse pas ρ D / sqrt(D² - ρ²) : R est fixé pour que cette borne tienne
// dans 90 % de la demi-hauteur (et de la demi-largeur), quelle que soit la
// rotation. Caméra privée (la dérive de la caméra partagée décadrerait).

const TAU = Math.PI * 2;
const TILES_U = 24, TILES_V = 4;
const WIDTH_FRAC = 0.35;                 // the strip's full width / R
const FOV_DEG = 24;
const CAM_D = 1 / Math.tan(FOV_DEG * Math.PI / 360);   // the z = 0 plane spans the screen's height
const ROT_BEATS = 32;
const MORPH_BEATS = 2, FLAT_BEATS = 1, RETWIST_BEATS = 2;
const RUNNER_SEGS = 24, RUNNER_LEN = 0.50;            // body length, in units of R
const MAX_RUNNERS = 12;
const TRAIL_BEATS = 2;
const NO_BPM = 128, SNAP_BEATS = 0.35, BUILD_BEATS = 32;
const DROP_SCORE_MIN = 0.7, DROP_STALE_MS = 1500;
const TIER = {
  0: { nu: 128, nv: 8, runners: 4, edge: 256, msaa: false },
  1: { nu: 256, nv: 16, runners: 6, edge: 512, msaa: true },
  2: { nu: 256, nv: 16, runners: 6, edge: 512, msaa: true }
};

function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
// R for a bound L (screen units) seen from D: the ball of radius rho = R (1 + w/R) projects within rho D / sqrt(D² - rho²)
export function fitRadius(L) {
  const rho = L * CAM_D / Math.sqrt(CAM_D * CAM_D + L * L);
  return rho / (1 + WIDTH_FRAC / 2);
}

// --- GLSL: the surface ---------------------------------------------------------
// uK: half-twists (continuous), uPinch: the seam's pinch, uR, uW: radius and half-width
const SURFACE_GLSL = `
  uniform float uK;
  uniform float uPinch;
  uniform float uR;
  uniform float uW;
  uniform mat3 uRot;
  // the seam's window: 1 at u = 0 (mod 2pi), 0 opposite; smooth
  float seamWin(float u) { float c = 0.5 + 0.5 * cos(u); return c * c * c; }
  float seamWinD(float u) { float c = 0.5 + 0.5 * cos(u); return -1.5 * c * c * sin(u); }
  // position and analytic partials at (U, v), U may run over [0, 4pi)
  void surface(float U, float v, out vec3 P, out vec3 Pu, out vec3 Pv) {
    float th = 0.5 * uK * U, dth = 0.5 * uK;
    vec3 rad = vec3(cos(U), sin(U), 0.0), tan = vec3(-sin(U), cos(U), 0.0), z = vec3(0.0, 0.0, 1.0);
    vec3 e = cos(th) * rad + sin(th) * z;
    vec3 de = dth * (-sin(th) * rad + cos(th) * z) + cos(th) * tan;
    float w = uW * (1.0 - uPinch * seamWin(U));
    float dw = -uW * uPinch * seamWinD(U);
    P = uR * rad + w * v * e;
    Pu = uR * tan + w * v * de + dw * v * e;
    Pv = w * e;
  }
`;
const SAFE_GLSL = `
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  float safeAtt(vec2 s) {
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(0.45, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
`;

const surfVertexShader = `
  precision highp float;
  ${SURFACE_GLSL}
  varying vec2 vUV;          // u in [0, 2pi), v in [-1, 1]
  varying vec3 vNv;
  varying vec3 vWorld;
  void main() {
    float u = position.x * 6.2831853, v = position.y * 2.0 - 1.0;
    vec3 P, Pu, Pv;
    surface(u, v, P, Pu, Pv);
    vec3 n = normalize(cross(Pu, Pv) + vec3(1e-7));
    vec3 w = uRot * P;
    vUV = vec2(u, v);
    vNv = normalize((viewMatrix * vec4(uRot * n, 0.0)).xyz);
    vWorld = w;
    gl_Position = projectionMatrix * viewMatrix * vec4(w, 1.0);
  }
`;
const surfFragmentShader = `
  precision highp float;
  uniform sampler2D uMat;
  uniform vec3 uTileCol;
  uniform vec3 uRunCol[${MAX_RUNNERS}];
  uniform vec2 uRun[${MAX_RUNNERS}];      // head U (0..4pi), v
  uniform float uRunN;
  uniform float uRunSpeed;                 // U per beat
  uniform vec3 uBeat;                      // beat phase, bar column, bar flash
  uniform float uGain;
  varying vec2 vUV;
  varying vec3 vNv;
  varying vec3 vWorld;
  void main() {
    // the lacquered black back (two-sided: the strip has one face)
    vec3 n = normalize(vNv);
    if (!gl_FrontFacing) n = -n;
    if (n.z < 0.0) n = normalize(vec3(n.xy, 0.02));
    vec4 m = texture2D(uMat, n.xy * 0.49 + 0.5);
    float body = m.b * m.b * ${MATCAP_GAIN.toFixed(2)};
    vec3 col = vec3(0.020, 0.022, 0.030) + vec3(0.10, 0.11, 0.14) * body + vec3(0.55, 0.58, 0.65) * m.a * ${CHROME_BOX.toFixed(2)} * 0.32;   // under the bloom's threshold: wide lacquer glints fed the veil

    // the tiles on the parametrisation
    vec2 t = vec2(vUV.x / 6.2831853 * ${TILES_U}.0, (vUV.y + 1.0) * 0.5 * ${TILES_V}.0);
    vec2 cell = floor(t), f = fract(t);
    vec2 dEdge = min(f, 1.0 - f) / max(fwidth(t), vec2(1e-5));     // px to the nearest tile border
    float dpx = min(dEdge.x, dEdge.y);
    float line = clamp(1.25 - dpx, 0.0, 1.0);                        // 1.5 px, its own coverage
    // the tile's light: the runners' trails, the beat's wave, the bar's row
    float uStart = cell.x / ${TILES_U}.0 * 6.2831853;
    float v0 = cell.y / ${TILES_V}.0 * 2.0 - 1.0, v1 = v0 + 2.0 / ${TILES_V}.0;
    vec3 lit = vec3(0.0);
    for (int j = 0; j < ${MAX_RUNNERS}; j++) {
      if (float(j) >= uRunN) break;
      vec2 r = uRun[j];
      // pass A: the runner on (u, v); pass B: on (u, -v), half a lap of 4pi later
      float ageA = mod(r.x - uStart, 12.5663706) / uRunSpeed;
      float ageB = mod(r.x - uStart - 6.2831853, 12.5663706) / uRunSpeed;
      float inA = step(v0, r.y) * step(r.y, v1), inB = step(v0, -r.y) * step(-r.y, v1);
      float a = max(inA * max(0.0, 1.0 - ageA / ${TRAIL_BEATS.toFixed(1)}), inB * max(0.0, 1.0 - ageB / ${TRAIL_BEATS.toFixed(1)}));
      lit = max(lit, uRunCol[j] * a);
    }
    float du = mod(cell.x / ${TILES_U}.0 - uBeat.x, 1.0);
    float wave = exp(-du * du / 0.004) * 0.35;
    float row = abs(cell.x - uBeat.y) < 0.5 ? uBeat.z : 0.0;
    vec3 fill = lit * 0.40 + uTileCol * (wave * 0.6 + row * 0.7);
    col += fill * (0.35 + 0.65 * smoothstep(0.0, 6.0, dpx));
    vec3 neon = mix(uTileCol, vec3(1.0), 0.15) * (0.55 + 0.45 * max(wave * 2.0, row));
    col = mix(col, neon, line);
    gl_FragColor = vec4(col * uGain, 1.0);
  }
`;
// screen-space ribbons for the edge and the runners: each vertex is a point of
// a curve on the surface; offset across in pixels; depth nudged toward the eye
const RIBBON_GLSL = `
  uniform vec2 uRes;
  vec4 clipOf(vec3 w) { return projectionMatrix * viewMatrix * vec4(w, 1.0); }
  vec4 offsetClip(vec4 c, vec4 cNext, float side, float px) {
    vec2 a = c.xy / c.w, b = cNext.xy / cNext.w;
    vec2 d = (b - a) * uRes;
    d = dot(d, d) > 1e-10 ? normalize(d) : vec2(1.0, 0.0);
    vec2 nrm = vec2(-d.y, d.x);
    vec4 o = c;
    o.xy += nrm * side * px * 2.0 / uRes * c.w;
    o.z -= 0.004 * o.w;
    return o;
  }
`;
const edgeVertexShader = `
  precision highp float;
  ${SURFACE_GLSL}
  ${RIBBON_GLSL}
  uniform float uEdgeN;
  uniform float uEdgePx;
  varying float vAcross;
  void main() {
    float i = position.x, side = position.y;
    float U = i / uEdgeN * 12.5663706, U2 = (i + 1.0) / uEdgeN * 12.5663706;
    vec3 P, Pu, Pv, Q, Qu, Qv;
    surface(U, 1.0, P, Pu, Pv);
    surface(U2, 1.0, Q, Qu, Qv);
    vec4 c = clipOf(uRot * P), cn = clipOf(uRot * Q);
    vAcross = side * (uEdgePx + 4.0);
    gl_Position = offsetClip(c, cn, side, uEdgePx + 4.0);
  }
`;
const edgeFragmentShader = `
  precision highp float;
  uniform vec3 uEdgeCol;
  uniform float uEdgePx;
  uniform float uGain;
  varying float vAcross;
  void main() {
    float d = abs(vAcross), h = 0.5 * uEdgePx;
    float cov = clamp(h + 0.5 - d, 0.0, 1.0);
    float core = 1.0 - smoothstep(max(h - 2.0, 0.6) - 0.6, max(h - 2.0, 0.6) + 0.6, d);
    float halo = 0.35 * exp(-max(d - h, 0.0) / 1.6) * (1.0 - smoothstep(3.0, 4.0, max(d - h, 0.0)));
    // the core just over the bloom's threshold: the edge is one loop twice round,
    // a full-white core on all of it bloomed into a veil over the black
    vec3 c = mix(uEdgeCol * 0.7, vec3(0.86, 0.83, 0.78), core) * cov + uEdgeCol * halo * 0.7;
    gl_FragColor = vec4(c * uGain, max(cov, halo));
  }
`;
const runnerVertexShader = `
  precision highp float;
  ${SURFACE_GLSL}
  ${RIBBON_GLSL}
  attribute float iRunner;
  uniform vec2 uRun[${MAX_RUNNERS}];
  uniform float uRunN;
  uniform float uBodyLen;          // world units
  uniform float uRunPx;
  varying float vAcross;
  varying float vAlong;
  varying float vRunner;
  void main() {
    int j = int(iRunner + 0.5);
    if (iRunner >= uRunN) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
    vec2 r = uRun[j];
    float k = position.x, side = position.y;     // k: 0 head .. ${RUNNER_SEGS} tail
    // R16: march from the head back at CONSTANT ARC LENGTH, du = ds / |P_U|
    float ds = uBodyLen / ${RUNNER_SEGS}.0;
    float U = r.x, Uk = U;
    vec3 P, Pu, Pv;
    for (int s = 0; s < ${RUNNER_SEGS}; s++) {
      if (float(s) >= k) break;
      surface(U, r.y, P, Pu, Pv);
      U -= ds / max(length(Pu), 1e-4);
    }
    surface(U, r.y, P, Pu, Pv);
    vec3 Q, Qu, Qv;
    surface(U + ds / max(length(Pu), 1e-4), r.y, Q, Qu, Qv);   // toward the head: the direction
    vec4 c = clipOf(uRot * P), cn = clipOf(uRot * Q);
    float t = k / ${RUNNER_SEGS}.0;
    float px = uRunPx * (1.0 - 0.75 * t) + 4.0;    // a capsule: thick head, thin tail, + halo room
    vAcross = side * px; vAlong = t; vRunner = iRunner;
    gl_Position = offsetClip(c, cn, -side, px);
  }
`;
const runnerFragmentShader = `
  precision highp float;
  uniform vec3 uRunCol[${MAX_RUNNERS}];
  uniform float uRunPx;
  uniform float uPulse;
  uniform float uGain;
  varying float vAcross;
  varying float vAlong;
  varying float vRunner;
  void main() {
    vec3 col = uRunCol[0];
    for (int j = 1; j < ${MAX_RUNNERS}; j++) if (abs(vRunner - float(j)) < 0.5) col = uRunCol[j];
    float h = 0.5 * uRunPx * (1.0 - 0.75 * vAlong);
    float d = abs(vAcross);
    float cov = clamp(h + 0.5 - d, 0.0, 1.0);
    float core = (1.0 - smoothstep(h * 0.45 - 0.6, h * 0.45 + 0.6, d)) * (1.0 - vAlong * 0.8);
    float fade = 1.0 - smoothstep(0.55, 1.0, vAlong);
    float halo = 0.35 * exp(-max(d - h, 0.0) / 1.5) * (1.0 - smoothstep(3.0, 4.0, max(d - h, 0.0)));
    vec3 c = (mix(col, vec3(0.90, 0.87, 0.83), core) * cov + col * halo * 0.7) * fade * (1.0 + 0.30 * uPulse);
    gl_FragColor = vec4(c * uGain, max(cov, halo) * fade);
  }
`;
const quadVertexShader = `
  precision highp float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
const displayFragmentShader = `
  precision highp float;
  ${SAFE_GLSL}
  uniform sampler2D uTex;
  uniform float uFlash;
  varying vec2 vUv;
  void main() {
    vec2 s = (vUv * 2.0 - 1.0) * vec2(uAspect, 1.0);
    vec3 c = texture2D(uTex, vUv).rgb * safeAtt(s);
    c = mix(c, vec3(${VDJ_DROP_FLASH.toFixed(2)}), uFlash);
    gl_FragColor = vec4(c, 1.0);
  }
`;

// a strip of quads: (i, side) per vertex, n segments
function stripGeometry(n) {
  const pos = new Float32Array((n + 1) * 2 * 3), idx = [];
  for (let i = 0; i <= n; i++) {
    pos.set([i, -1, 0], (i * 2) * 3); pos.set([i, 1, 0], (i * 2 + 1) * 3);
    if (i < n) { const a = i * 2; idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2); }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setIndex(idx);
  return g;
}

export class MobiusVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null && TIER[o.qualityTier] ? o.qualityTier : 1;
    this._L = TIER[this._tier];
    const tw = parseInt(urlParam('mobiustwists'), 10);
    this._forceTwists = tw === 1 || tw === 3 ? tw : null;
    const rn = parseInt(urlParam('mobiusrunners'), 10);
    this._forceRunners = Number.isFinite(rn) ? Math.max(0, Math.min(MAX_RUNNERS, rn)) : null;
    this._forceFlat = urlParam('mobiusflat') === '1';
    this._forceDrop = urlParam('mobiusdrop') === '1';
    this._forceBuild = urlParam('mobiusbuild') === '1';
    this._post = new VdjPost({ bloomStrength: 0.18,      // the long neon edge loop bloomed into a green veil at 0.45
      bloomThreshold: 0.78, grain: 0, vignette: 0.35, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 });
    this._comfort = new VdjComfort();
    if (urlParam('mobiusprobe') === '1' && typeof window !== 'undefined') window.__mobius = this;
  }

  get postProcessing() { return this._post.value; }

  init(scene) {
    this._scene = scene;
    const L = this._L;
    this._cam = new THREE.PerspectiveCamera(FOV_DEG, 1.78, 0.1, 50);
    this._private = new THREE.Scene();
    const m = genererCanauxRuban(256);
    this._matcap = new THREE.DataTexture(m.data, m.size, m.size, THREE.RGBAFormat);
    this._matcap.minFilter = this._matcap.magFilter = THREE.LinearFilter; this._matcap.needsUpdate = true;

    const runCols = Array.from({ length: MAX_RUNNERS }, () => new THREE.Color());
    const run = Array.from({ length: MAX_RUNNERS }, () => new THREE.Vector2());
    const U = this._U = {
      uK: { value: 1 }, uPinch: { value: 0 }, uR: { value: 0.7 }, uW: { value: 0.7 * WIDTH_FRAC / 2 }, uRot: { value: new THREE.Matrix3() },
      uMat: { value: this._matcap }, uTileCol: { value: new THREE.Color(0, 0.8, 1) }, uRunCol: { value: runCols }, uRun: { value: run },
      uRunN: { value: L.runners }, uRunSpeed: { value: TAU / 4 }, uBeat: { value: new THREE.Vector3() }, uGain: { value: 1 },
      uRes: { value: new THREE.Vector2(1920, 1080) }, uEdgeN: { value: L.edge }, uEdgePx: { value: 4 }, uEdgeCol: { value: new THREE.Color(1, 0.2, 0.7) },
      uBodyLen: { value: 0.2 }, uRunPx: { value: 6 }, uPulse: { value: 0 }
    };

    // the surface: an (nu+1) x (nv+1) grid of (u, v) in [0, 1]
    const sg = new THREE.PlaneGeometry(1, 1, L.nu, L.nv);
    const p = sg.getAttribute('position');
    for (let i = 0; i < p.count; i++) p.setXYZ(i, p.getX(i) + 0.5, p.getY(i) + 0.5, 0);
    this._surf = new THREE.Mesh(sg, new THREE.ShaderMaterial({ uniforms: U, vertexShader: surfVertexShader, fragmentShader: surfFragmentShader, side: THREE.DoubleSide }));
    this._surf.frustumCulled = false;
    // the edge: ONE loop, v = 1 over U in [0, 4pi)
    this._edge = new THREE.Mesh(stripGeometry(L.edge), new THREE.ShaderMaterial({
      uniforms: U, vertexShader: edgeVertexShader, fragmentShader: edgeFragmentShader,
      transparent: true, depthWrite: false, side: THREE.DoubleSide,
      blending: THREE.CustomBlending, blendEquation: THREE.MaxEquation, blendSrc: THREE.OneFactor, blendDst: THREE.OneFactor
    }));
    this._edge.frustumCulled = false; this._edge.renderOrder = 1;
    // the runners: one strip per runner, instanced
    const rs = stripGeometry(RUNNER_SEGS);
    const rg = new THREE.InstancedBufferGeometry();
    rg.setAttribute('position', rs.getAttribute('position')); rg.setIndex(rs.getIndex());
    const ir = new Float32Array(MAX_RUNNERS); for (let j = 0; j < MAX_RUNNERS; j++) ir[j] = j;
    rg.setAttribute('iRunner', new THREE.InstancedBufferAttribute(ir, 1));
    rg.instanceCount = MAX_RUNNERS;
    this._runnerStrip = rs;
    this._runners = new THREE.Mesh(rg, new THREE.ShaderMaterial({
      uniforms: U, vertexShader: runnerVertexShader, fragmentShader: runnerFragmentShader,
      transparent: true, depthWrite: false, side: THREE.DoubleSide,
      blending: THREE.CustomBlending, blendEquation: THREE.MaxEquation, blendSrc: THREE.OneFactor, blendDst: THREE.OneFactor
    }));
    this._runners.frustumCulled = false; this._runners.renderOrder = 2;
    this._private.add(this._surf, this._edge, this._runners);

    // MSAA private target, a display quad (with the cover's mask, R19: it follows the cover)
    this._msaaTarget = null;
    this._dispU = Object.assign({ uTex: { value: null }, uFlash: { value: 0 } }, {
      uAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
    });
    this._display = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({
      uniforms: this._dispU, vertexShader: quadVertexShader, fragmentShader: displayFragmentShader, depthTest: false, depthWrite: false
    }));
    this._display.frustumCulled = false; this._display.renderOrder = -1000;
    const self = this;
    this._display.onBeforeRender = function (renderer) { self._render(renderer); };
    scene.add(this._display);

    // state
    this._clock = 0; this._clockPrev = 0; this._targetOffset = null;
    this._beatCount = 0; this._barCount = 0; this._phaseBeat = null; this._phaseBar = null; this._phasePhrase = null;
    this._bpm = NO_BPM; this._time = 0;
    this._k = this._forceTwists || 1; this._kFrom = this._k; this._kTo = this._k; this._morphStart = null;
    this._runU = 0;               // the runners' common progress (U), integrated
    this._drop = null; this._flashFrames = 0;
    this._marksRef = undefined; this._drops = []; this._dropIdx = 0; this._lastNowMs = null;
    this._paletteKey = null; this._palShift = 0; this._lastPhraseAt = 0; this._lastBarS = -9;
    this._log = [];
  }

  _note(e) { this._log.push(e); if (this._log.length > 40) this._log.shift(); }

  _tickDrops(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks; this._drops = [];
      if (marks) for (const m of marks) if (m.type === 'drop' && m.score >= DROP_SCORE_MIN) this._drops.push(m.atMs);
      this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return false;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) {
      this._dropIdx = 0;
      while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) this._dropIdx++;
    }
    this._lastNowMs = now;
    let fired = false;
    while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) {
      if (now - this._drops[this._dropIdx] < DROP_STALE_MS) fired = true;
      this._dropIdx++;
    }
    return fired;
  }

  _colours(profile) {
    const pal = profile.artworkPalette;
    const src = (pal && (pal.vividColors || pal.dominantColors)) || [];
    const pick = (k) => src.length ? vdjColor(src[k % src.length]) : new THREE.Color().setHSL((0.55 + k * 0.31) % 1, 0.9, 0.55);
    const s = this._palShift;
    const tile = pick(s), edge = pick(s + 1), hsl = { h: 0, s: 0, l: 0 };
    tile.getHSL(hsl);
    // the edge at least 0.25 away in hue (R4)
    const he = { h: 0, s: 0, l: 0 }; edge.getHSL(he);
    if (Math.min(Math.abs(he.h - hsl.h), 1 - Math.abs(he.h - hsl.h)) < 0.25) edge.setHSL((hsl.h + 0.4) % 1, 0.9, 0.55);
    this._U.uTileCol.value.copy(tile); this._U.uEdgeCol.value.copy(edge);
    // the runners: hues spread from the tiles', all saturated
    for (let j = 0; j < MAX_RUNNERS; j++) this._U.uRunCol.value[j].setHSL((hsl.h + 0.18 + j * 0.61803) % 1, 0.95, 0.55);
  }

  update(deltaTime, profile) {
    const dt = Math.min(Math.max(deltaTime, 0), 0.1);
    const U = this._U, L = this._L;
    const pk = (profile.artworkPalette && String((profile.artworkPalette.vividColors || []).slice(0, 3))) + '/' + this._palShift;
    if (pk !== this._paletteKey) { this._paletteKey = pk; this._colours(profile); }
    this._fit();
    if (profile.playbackState !== 'PLAYING') { this._dispU.uFlash.value = 0; return; }   // frozen, never black
    this._time += dt;

    // the clock, in beats, on the music's bars
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    const bp = profile.beatPhase, barIn = profile.barPhase, ph = profile.phrasePhase;
    const hasGrid = bp != null && bpm > 0;
    this._bpm = hasGrid ? bpm : NO_BPM;
    const beatS = 60 / this._bpm;
    this._clockPrev = this._clock;
    if (hasGrid) {
      if (this._phaseBeat != null && bp < this._phaseBeat - 0.5) this._beatCount++;
      this._phaseBeat = bp;
      let target;
      if (barIn != null) {
        if (this._phaseBar != null && barIn < this._phaseBar - 0.5) this._barCount++;
        this._phaseBar = barIn;
        target = 4 * (this._barCount + barIn);
      } else target = this._beatCount + bp;
      if (this._targetOffset == null) this._targetOffset = 4 * Math.round((this._clock - target) / 4);
      const err = target + this._targetOffset - this._clock;
      if (Math.abs(err) > SNAP_BEATS) { this._targetOffset = 4 * Math.round((this._clock - target) / 4); this._clock = target + this._targetOffset; }
      else this._clock += Math.max(dt / beatS * 0.5, dt / beatS + err * Math.min(1, dt / 0.5));
    } else { this._clock += dt / beatS; this._targetOffset = null; }
    const c = this._clock, cp = this._clockPrev;
    const edge = (k) => Math.floor(cp / k) !== Math.floor(c / k) && c > cp;

    // the rise: faster runners, more of them (up to 12)
    const nx = profile.director && profile.director.nextEvent;
    let build = 0;
    if (nx && nx.type === 'drop' && nx.score >= DROP_SCORE_MIN && nx.msUntil > 0) build = Math.max(0, 1 - nx.msUntil / 1000 / (BUILD_BEATS * beatS));
    if (this._forceBuild) build = 0.5 + 0.5 * Math.sin(this._time * 0.5);

    // the phrase: 1 <-> 3 half-twists in 2 beats, the next colours
    let phraseEdge = false;
    if (hasGrid && ph != null) { if (this._phasePhrase != null && ph < this._phasePhrase - 0.5) phraseEdge = true; this._phasePhrase = ph; }
    else if (edge(16)) phraseEdge = true;
    if (!phraseEdge && edge(4) && c - this._lastPhraseAt >= 32) phraseEdge = true;
    if (phraseEdge && !this._drop && this._morphStart == null) {
      this._lastPhraseAt = c;
      this._kFrom = this._k; this._kTo = this._forceTwists || (this._k === 1 ? 3 : 1);
      this._morphStart = c;
      this._palShift++;
      this._note({ what: 'phrase', beat: +c.toFixed(2), twists: this._kTo });
    }

    // the drop: unrolled into a flat ring facing the camera in one beat, flash, twisted back
    let drop = hasGrid && this._tickDrops(profile);
    if (this._forceDrop && edge(4) && Math.floor(c / 4) % 8 === 4) drop = true;
    if (drop && !this._drop) {
      this._drop = { start: c };
      if (this._comfort.requestFlash(this._time)) this._flashFrames = 1;
      this._note({ what: 'drop', beat: +c.toFixed(2) });
    }

    // the twist: the phrase's morph, and the drop's flattening
    let k = this._k, flat = 0;
    if (this._morphStart != null) {
      const t = Math.min(1, (c - this._morphStart) / MORPH_BEATS), e = t * t * (3 - 2 * t);
      k = this._kFrom + (this._kTo - this._kFrom) * e;
      if (t >= 1) { this._k = this._kTo; this._morphStart = null; k = this._k; }
    }
    if (this._drop) {
      const t = c - this._drop.start;
      if (t < FLAT_BEATS) flat = smooth(t / FLAT_BEATS);
      else if (t < FLAT_BEATS + 1) flat = 1;
      else if (t < FLAT_BEATS + 1 + RETWIST_BEATS) flat = 1 - smooth((t - FLAT_BEATS - 1) / RETWIST_BEATS);
      else this._drop = null;
    }
    if (this._forceFlat) flat = 1;
    const kEff = k * (1 - flat);
    U.uK.value = kEff;
    // the seam's pinch: as wide as the section's mismatch there (2 pi k / 2 mod pi)
    const mis = Math.abs(Math.sin(Math.PI * kEff));
    U.uPinch.value = Math.pow(mis, 0.35);

    // the rotation, two axes: a turn in 32 beats about the ring's own axis (the
    // twist travels round), and a tilt that sways +-25 degrees about a second
    // axis over the same 32 beats. A turn about an in-plane axis put the ring
    // edge-on once a lap and the strip read as a flat band
    const a = TAU * c / ROT_BEATS;
    const qSpin = new THREE.Quaternion().setFromAxisAngle(_zAxis, a);
    const qTilt = new THREE.Quaternion().setFromEuler(new THREE.Euler(-0.55 + 0.30 * Math.sin(a), 0.35 * Math.cos(a), 0, 'XYZ'));
    const qa = qTilt.multiply(qSpin);
    qa.slerp(_qFace, flat);
    U.uRot.value.setFromMatrix4(_m4.makeRotationFromQuaternion(qa));

    // the runners: a lap (2 pi) per bar, faster in the rise; spread over the 4 pi double lap
    const speed = TAU / 4 * (1 + 1.5 * build);                       // U per beat
    this._runU += dt / beatS * speed;
    const n = this._forceRunners != null ? this._forceRunners : Math.round(L.runners + (MAX_RUNNERS - L.runners) * build);
    U.uRunN.value = n; U.uRunSpeed.value = speed;
    for (let j = 0; j < MAX_RUNNERS; j++) {
      const Uh = (this._runU + j * 4 * Math.PI / Math.max(n, 1)) % (4 * Math.PI);
      const v = [-0.62, 0.12, 0.78, -0.25, 0.45, -0.85, 0.30, -0.50, 0.62, -0.05, 0.88, -0.70][j];
      U.uRun.value[j].set(Uh, v);
    }
    // the beat: tile wave, runner pulse, RGB split; the bar: a row flash, camera kick
    const bph = c - Math.floor(c);
    if (edge(4)) this._lastBarS = this._time;
    U.uBeat.value.set(bph, Math.floor(c / 4) % TILES_U, Math.exp(-(this._time - this._lastBarS) / 0.25));
    U.uPulse.value = Math.exp(-bph * beatS / 0.12);
    this._post.update(vdjBeatChroma(bph, beatS));
    const kick = vdjCameraKick(barIn != null ? barIn : (c / 4) % 1, Math.floor(c / 4), 4);
    this._kick = kick;
    this._dispU.uFlash.value = this._flashFrames > 0 ? 1 : 0;
    if (this._flashFrames > 0) this._flashFrames--;
  }

  // R: the ribbon's ball projects inside 90 % of the half height (and width), about the stage centre
  _fit() {
    const aspect = safeZoneUniforms.uAspect.value || 1.78;
    const Lb = Math.min(0.9, aspect - 0.1) * 0.97;
    const R = fitRadius(Lb);
    this._U.uR.value = R; this._U.uW.value = R * WIDTH_FRAC / 2;
    this._U.uBodyLen.value = R * RUNNER_LEN;
  }

  _render(renderer) {
    const cur = renderer.getRenderTarget();
    let w, h;
    if (cur) { w = cur.width; h = cur.height; } else { const b = renderer.getDrawingBufferSize(_size); w = b.x; h = b.y; }
    const cam = this._cam, kick = this._kick || { zoom: 1, roll: 0 };
    cam.aspect = w / h; cam.fov = FOV_DEG / kick.zoom;
    // the stage centre (R19): the camera aims so the ribbon's centre sits there
    const st = safeZoneUniforms.uStageCenter.value;
    cam.position.set(-st.x, -st.y, CAM_D);
    cam.up.set(Math.sin(kick.roll), Math.cos(kick.roll), 0);
    cam.lookAt(-st.x, -st.y, 0);
    cam.updateProjectionMatrix();
    this._U.uRes.value.set(w, h);
    const s = h / 1080;
    this._U.uEdgePx.value = Math.max(3, 4 * s);
    this._U.uRunPx.value = Math.max(5, 10 * s);
    if (!this._msaaTarget || this._msaaTarget.width !== w || this._msaaTarget.height !== h) {
      if (this._msaaTarget) this._msaaTarget.dispose();
      this._msaaTarget = new THREE.WebGLRenderTarget(w, h, { samples: this._L.msaa ? 4 : 0, depthBuffer: true, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter });
    }
    const cc = renderer.getClearColor(_cc), ca = renderer.getClearAlpha();
    const xr = renderer.xr.enabled; renderer.xr.enabled = false;
    renderer.setRenderTarget(this._msaaTarget);
    renderer.setClearColor(0x000000, 1);
    renderer.clear(true, true, false);
    renderer.render(this._private, cam);
    renderer.setRenderTarget(cur);
    renderer.setClearColor(cc, ca);
    renderer.xr.enabled = xr;
    this._dispU.uTex.value = this._msaaTarget.texture;
  }

  dispose() {
    if (this._scene && this._display) { this._display.onBeforeRender = null; this._scene.remove(this._display); }
    for (const m of [this._display, this._surf, this._edge, this._runners]) if (m) { m.geometry.dispose(); m.material.dispose(); }
    if (this._runnerStrip) this._runnerStrip.dispose();
    if (this._matcap) this._matcap.dispose();
    if (this._msaaTarget) this._msaaTarget.dispose();
    if (typeof window !== 'undefined' && window.__mobius === this) window.__mobius = null;
    this._scene = null;
  }
}

function smooth(x) { const t = Math.max(0, Math.min(1, x)); return t * t * (3 - 2 * t); }
const _size = new THREE.Vector2();
const _cc = new THREE.Color();
const _m4 = new THREE.Matrix4();
const _zAxis = new THREE.Vector3(0, 0, 1);
const _qFace = new THREE.Quaternion();          // identity: the ring's plane (xy) faces the camera (on +z)
